<template>
  <div class="mc-liveview__map">
    <div class="mc-map--route-switch">
      <McButton
        :variant="`${priority === 0 ? 'primary' : 'secondary'}`"
        size="sm"
        @click="generateLine(geoData, 0)"
        :disabled="primaryLoc.length ? false : true"
        icon="icon-outline_pin-history"
        :text="$t('master.list.route.routePrimary')"
        block
      />
      <McButton
        :variant="`${priority !== 0 ? 'primary' : 'secondary'}`"
        size="sm"
        @click="generateLine(geoData, 1)"
        :disabled="alternateLoc.length ? false : true"
        icon="icon-outline_pin-history"
        :text="$t('master.list.route.routeAlternative')"
        block
      />
    </div>
    <GmapMap
      id="map"
      ref="MapPlaning"
      :center="center"
      :zoom="zoom"
      :options="gMapOptions"
      class="mc-map mc-map--route"
    >
      <template v-if="priority === 0 && primaryLoc.length">
        <gmap-custom-marker
          v-for="(row, index) in primaryLoc"
          :key="index"
          :marker="{
            lat: Number(row.lat),
            lng: Number(row.lng),
          }"
          :offsetY="40"
        >
          <div v-if="primaryLoc" class="text-center mc-map--route-marker">
            <img alt="marker" src="@/assets/images/caricon/primary-loc.svg" />
            <span>{{ index + 1 }}</span>
          </div>
        </gmap-custom-marker>
      </template>
      <template v-else-if="priority === 1 && alternateLoc.length">
        <gmap-custom-marker
          v-for="(row, index) in alternateLoc"
          :key="index"
          :marker="{
            lat: Number(row.lat),
            lng: Number(row.lng),
          }"
          :offsetY="40"
        >
          <div v-if="alternateLoc" class="text-center mc-map--route-marker">
            <img alt="marker" src="@/assets/images/caricon/alt-loc.svg" />
            <span>{{ index + 1 }}</span>
          </div>
        </gmap-custom-marker>
      </template>
    </GmapMap>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import responsive from '@/mixin/responsive'
import * as routeApi from '@/services/rest-api-vsms/lib/route/route'
import { gmapApi } from 'vue2-google-maps'
import GmapCustomMarker from 'vue2-gmap-custom-marker'
import McButton from '@/components/McButton'

export default {
  name: 'GMapPlaning',
  components: {
    McButton,
    'gmap-custom-marker': GmapCustomMarker,
  },
  mixins: [Helper, responsive],
  props: {
    geometry: Object,
  },
  data() {
    return {
      routeApi: routeApi,

      map: null,
      gMapOptions: {},
      center: { lat: -2.958881, lng: 109.5543666 },
      zoom: 5.56,
      directionsService: null,
      directionsRenderer: null,
      allPathPrimary: [],
      allPathSecondary: [],
      primaryLoc: [],
      alternateLoc: [],

      geoData: {},
      priority: 0,
      loading: false,
    }
  },

  computed: {
    google: gmapApi,
  },

  watch: {
    geometry: {
      async handler(val) {
        if (val?.id) {
          this.primaryLoc.splice(0, this.primaryLoc.length)
          this.alternateLoc.splice(0, this.alternateLoc.length)
          await this.resetPolyline()
          let params = null
          if (
            val.routeRecordStatus == 'progress' ||
            val.routeRecordStatus == 'done' ||
            (val.routeRecordStatus == 'cancelled' && val.startOn != '' && val.startOn != null)
          )
            params = {
              source: 'archived',
              limit: val.startOn,
            }

          this.priority = 0
          this.geoData = await routeApi.showDirection(val.id, params)
          this.generateLine(this.geoData, 0)

          val.route_plan.forEach((row, index) => {
            row.geofences.forEach((geo) => {
              if (geo.type === 'C') {
                const circle = geo.coordinates.split(',')
                const latLng = {
                  lat: Number(circle[0].replace('<(', '')),
                  lng: Number(circle[1].replace(')', '')),
                }

                if (index === 0) {
                  this.primaryLoc.push(latLng)
                } else {
                  this.alternateLoc.push(latLng)
                }
              } else if (geo.type === 'P') {
                const poly = geo.coordinates.split('),(')
                const bounds = new this.google.maps.LatLngBounds()
                poly.forEach((element) => {
                  const latlng = element.split(',')
                  bounds.extend({
                    lat: Number(latlng[0].replace('((', '')),
                    lng: Number(latlng[1].replace('))', '')),
                  })
                })
                const latLng = {
                  lat: bounds.getCenter().lat(),
                  lng: bounds.getCenter().lng(),
                }

                if (index === 0) {
                  this.primaryLoc.push(latLng)
                } else {
                  this.alternateLoc.push(latLng)
                }
              }
            })
          })
        }
      },
      deep: true,
      immediate: true,
    },
  },

  async mounted() {
    const self = this

    await this.$refs.MapPlaning.$mapPromise.then((map) => {
      let directionsService = new this.google.maps.DirectionsService()
      let directionsRenderer = new this.google.maps.DirectionsRenderer({
        suppressMarkers: false,
      })
      self.map = map

      this.directionsService = directionsService
      this.directionsRenderer = directionsRenderer

      self.gMapOptions = {
        minZoom: 3,
        mapTypeControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: self.google.maps.ControlPosition.LEFT_BOTTOM,
        },
        scaleControl: true,
        streetViewControl: true,
        streetViewControlOptions: {
          position: self.google.maps.ControlPosition.LEFT_BOTTOM,
        },
        fullscreenControl: false,
      }
    })
  },

  methods: {
    async generateLine({ data }, priority) {
      this.loading = true
      await this.resetPolyline()

      this.priority = priority
      const generalRoute = JSON.parse(data.route_plan[0].geometry)
      const alternativeRoute =
        data.route_plan.length > 1 ? JSON.parse(data.route_plan[1].geometry) : []

      if (priority === 0 || alternativeRoute.length === 0) {
        return this.createLine(generalRoute, alternativeRoute, priority)
      }

      return this.createLine(alternativeRoute, generalRoute, priority)
    },

    createLine(primaryRoute, secondaryRoute, priority) {
      let bounds = new this.google.maps.LatLngBounds()

      const primarylines = []
      for (let i = 0; i < primaryRoute.length - 1; i++) {
        primarylines.push(new this.google.maps.LatLng(primaryRoute[i][1], primaryRoute[i][0]))
        primarylines.push(
          new this.google.maps.LatLng(primaryRoute[i + 1][1], primaryRoute[i + 1][0])
        )
        bounds.extend(new this.google.maps.LatLng(primaryRoute[i][1], primaryRoute[i][0]))
      }

      this.map.fitBounds(bounds)

      // create a polyline
      let primaryPath = new this.google.maps.Polyline({
        path: primarylines,
        geodesic: true,
        strokeOpacity: 1.0,
        strokeWeight: 8,
      })

      this.allPathPrimary.push(primaryPath)
      // // set the color of the segment based on the lowest index point

      if (priority == 0) {
        primaryPath.setOptions({
          strokeColor: '#0D4491',
          zIndex: 99999,
        })
      } else {
        primaryPath.setOptions({
          strokeColor: '#72A8E9',
          zIndex: 99999,
        })
      }

      primaryPath.setMap(this.map)

      if (secondaryRoute?.length) {
        const secondarylines = []
        for (let i = 0; i < secondaryRoute.length - 1; i++) {
          secondarylines.push(
            new this.google.maps.LatLng(secondaryRoute[i][1], secondaryRoute[i][0])
          )
          secondarylines.push(
            new this.google.maps.LatLng(secondaryRoute[i + 1][1], secondaryRoute[i + 1][0])
          )
        }
        // create a polyline
        let secondaryPath = new this.google.maps.Polyline({
          path: secondarylines,
          geodesic: true,
          strokeOpacity: 1.0,
          strokeWeight: 8,
        })

        this.allPathSecondary.push(secondaryPath)
        // // set the color of the segment based on the lowest index point

        secondaryPath.setOptions({
          strokeColor: '#99A1B1',
        })

        secondaryPath.setMap(this.map)
      }

      this.loading = true
    },
    resetPolyline() {
      this.allPathPrimary.forEach((element) => {
        element.setMap(null)
      })
      this.allPathSecondary.forEach((element) => {
        element.setMap(null)
      })

      this.allPathPrimary.splice(0, this.allPathPrimary.length)
      this.allPathSecondary.splice(0, this.allPathSecondary.length)
    },
  },
}
</script>
